/**
 * Follow Query Hooks
 * React Query hooks for follow-related data fetching
 */

import { useQuery } from "@tanstack/react-query"
import * as followService from "../services/follow.service"

/**
 * Hook to check follow status for a target user
 * @param targetUserId - The user ID to check follow status for
 * @param skip - If true, skip fetching (useful when status is already known)
 */
export function useFollow(targetUserId: string, skip = false) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["follow-status", targetUserId],
    queryFn: () => followService.checkFollowStatus(targetUserId),
    enabled: !!targetUserId && !skip,
    refetchOnWindowFocus: false,
  })

  return {
    isFollowing: data?.isFollowing || false,
    isPending: data?.isPending || false,
    isLoading,
    error,
  }
}

/**
 * Hook to get followers list for a user
 * @param userId - The user ID to get followers for
 * @param page - Page number
 * @param enabled - Whether the query should be enabled
 */
export function useFollowers(userId: string, page = 1, enabled = true) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["followers", userId, page],
    queryFn: () => followService.getFollowers(userId, page),
    enabled: !!userId && enabled,
  })

  return {
    followers: data?.followers || [],
    total: data?.total || 0,
    pages: data?.pages || 1,
    isLoading,
    error,
  }
}

/**
 * Hook to get following list for a user
 * @param userId - The user ID to get following for
 * @param page - Page number
 * @param enabled - Whether the query should be enabled
 */
export function useFollowing(userId: string, page = 1, enabled = true) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["following", userId, page],
    queryFn: () => followService.getFollowing(userId, page),
    enabled: !!userId && enabled,
  })

  return {
    following: data?.following || [],
    total: data?.total || 0,
    pages: data?.pages || 1,
    isLoading,
    error,
  }
}
